import { http } from "@/lib/http";
import type { PrecheckReport } from "@/lib/prechecks";

import type {
  HealthMonitor,
  HealthMonitorCreateRequest,
  HealthMonitorUpdateRequest,
  L7Policy,
  L7PolicyCreateRequest,
  L7PolicyUpdateRequest,
  L7Rule,
  L7RuleCreateRequest,
  L7RuleUpdateRequest,
  LbStats,
  Listener,
  ListenerCreateRequest,
  ListenerUpdateRequest,
  LoadBalancer,
  LoadBalancerCreateRequest,
  LoadBalancerStatusTree,
  LoadBalancerUpdateRequest,
  LoadBalancerWizardRequest,
  LoadBalancerWizardResponse,
  Pool,
  PoolCreateRequest,
  PoolMember,
  PoolMemberCreateRequest,
  PoolMemberUpdateRequest,
  PoolUpdateRequest,
} from "./types";

// --- Load balancers --- //

export async function listLoadBalancers(): Promise<LoadBalancer[]> {
  const { data } = await http.get<LoadBalancer[]>("/lbaas/load-balancers");
  return data;
}

export async function getLoadBalancer(lbId: string): Promise<LoadBalancer> {
  const { data } = await http.get<LoadBalancer>(
    `/lbaas/load-balancers/${lbId}`,
  );
  return data;
}

export async function getLoadBalancerStats(lbId: string): Promise<LbStats> {
  const { data } = await http.get<LbStats>(
    `/lbaas/load-balancers/${lbId}/stats`,
  );
  return data;
}

export async function getLoadBalancerStatus(
  lbId: string,
): Promise<LoadBalancerStatusTree> {
  const { data } = await http.get<LoadBalancerStatusTree>(
    `/lbaas/load-balancers/${lbId}/status`,
  );
  return data;
}

export async function getListenerStats(
  listenerId: string,
): Promise<LbStats> {
  const { data } = await http.get<LbStats>(
    `/lbaas/listeners/${listenerId}/stats`,
  );
  return data;
}

export async function createLoadBalancer(
  payload: LoadBalancerCreateRequest,
): Promise<LoadBalancer> {
  const { data } = await http.post<LoadBalancer>(
    "/lbaas/load-balancers",
    payload,
  );
  return data;
}

export async function deleteLoadBalancer(
  lbId: string,
  cascade = true,
): Promise<void> {
  await http.delete(`/lbaas/load-balancers/${lbId}`, {
    params: { cascade },
  });
}

/** Rebuild a balancer's failed machines (ERROR, or a degraded HA balancer). */
export async function repairLoadBalancer(lbId: string): Promise<LoadBalancer> {
  const { data } = await http.post<LoadBalancer>(
    `/lbaas/load-balancers/${lbId}/repair`,
  );
  return data;
}

export async function updateLoadBalancer(
  lbId: string,
  payload: LoadBalancerUpdateRequest,
): Promise<LoadBalancer> {
  const { data } = await http.put<LoadBalancer>(
    `/lbaas/load-balancers/${lbId}`,
    payload,
  );
  return data;
}

/** Dry run of the wizard: every prerequisite and what to fix. Builds nothing. */
export async function precheckLoadBalancer(
  payload: LoadBalancerWizardRequest,
): Promise<PrecheckReport> {
  const { data } = await http.post<PrecheckReport>(
    "/lbaas/load-balancers/wizard/precheck",
    payload,
  );
  return data;
}

export async function provisionLoadBalancer(
  payload: LoadBalancerWizardRequest,
): Promise<LoadBalancerWizardResponse> {
  const { data } = await http.post<LoadBalancerWizardResponse>(
    "/lbaas/load-balancers/wizard",
    payload,
  );
  return data;
}

// --- Listeners --- //

export async function listListeners(): Promise<Listener[]> {
  const { data } = await http.get<Listener[]>("/lbaas/listeners");
  return data;
}

export async function createListener(
  payload: ListenerCreateRequest,
): Promise<Listener> {
  const { data } = await http.post<Listener>("/lbaas/listeners", payload);
  return data;
}

export async function updateListener(
  listenerId: string,
  payload: ListenerUpdateRequest,
): Promise<Listener> {
  const { data } = await http.put<Listener>(
    `/lbaas/listeners/${listenerId}`,
    payload,
  );
  return data;
}

export async function deleteListener(listenerId: string): Promise<void> {
  await http.delete(`/lbaas/listeners/${listenerId}`);
}

// --- Pools --- //

export async function listPools(): Promise<Pool[]> {
  const { data } = await http.get<Pool[]>("/lbaas/pools");
  return data;
}

export async function createPool(payload: PoolCreateRequest): Promise<Pool> {
  const { data } = await http.post<Pool>("/lbaas/pools", payload);
  return data;
}

export async function updatePool(
  poolId: string,
  payload: PoolUpdateRequest,
): Promise<Pool> {
  const { data } = await http.put<Pool>(`/lbaas/pools/${poolId}`, payload);
  return data;
}

export async function deletePool(poolId: string): Promise<void> {
  await http.delete(`/lbaas/pools/${poolId}`);
}

// --- Members --- //

export async function listMembers(poolId: string): Promise<PoolMember[]> {
  const { data } = await http.get<PoolMember[]>(
    `/lbaas/pools/${poolId}/members`,
  );
  return data;
}

export async function createMember(
  poolId: string,
  payload: PoolMemberCreateRequest,
): Promise<PoolMember> {
  const { data } = await http.post<PoolMember>(
    `/lbaas/pools/${poolId}/members`,
    payload,
  );
  return data;
}

export async function updateMember(
  poolId: string,
  memberId: string,
  payload: PoolMemberUpdateRequest,
): Promise<PoolMember> {
  const { data } = await http.put<PoolMember>(
    `/lbaas/pools/${poolId}/members/${memberId}`,
    payload,
  );
  return data;
}

export async function deleteMember(
  poolId: string,
  memberId: string,
): Promise<void> {
  await http.delete(`/lbaas/pools/${poolId}/members/${memberId}`);
}

// --- Health monitors --- //

export async function listHealthMonitors(): Promise<HealthMonitor[]> {
  const { data } = await http.get<HealthMonitor[]>("/lbaas/health-monitors");
  return data;
}

export async function createHealthMonitor(
  payload: HealthMonitorCreateRequest,
): Promise<HealthMonitor> {
  const { data } = await http.post<HealthMonitor>(
    "/lbaas/health-monitors",
    payload,
  );
  return data;
}

export async function updateHealthMonitor(
  monitorId: string,
  payload: HealthMonitorUpdateRequest,
): Promise<HealthMonitor> {
  const { data } = await http.put<HealthMonitor>(
    `/lbaas/health-monitors/${monitorId}`,
    payload,
  );
  return data;
}

export async function deleteHealthMonitor(monitorId: string): Promise<void> {
  await http.delete(`/lbaas/health-monitors/${monitorId}`);
}

// --- L7 policies --- //

export async function listL7Policies(listenerId?: string): Promise<L7Policy[]> {
  const { data } = await http.get<L7Policy[]>("/lbaas/l7policies", {
    params: listenerId ? { listener_id: listenerId } : undefined,
  });
  return data;
}

export async function createL7Policy(
  payload: L7PolicyCreateRequest,
): Promise<L7Policy> {
  const { data } = await http.post<L7Policy>("/lbaas/l7policies", payload);
  return data;
}

export async function updateL7Policy(
  policyId: string,
  payload: L7PolicyUpdateRequest,
): Promise<L7Policy> {
  const { data } = await http.put<L7Policy>(
    `/lbaas/l7policies/${policyId}`,
    payload,
  );
  return data;
}

export async function deleteL7Policy(policyId: string): Promise<void> {
  await http.delete(`/lbaas/l7policies/${policyId}`);
}

// --- L7 rules (nested under a policy) --- //

export async function listL7Rules(policyId: string): Promise<L7Rule[]> {
  const { data } = await http.get<L7Rule[]>(
    `/lbaas/l7policies/${policyId}/rules`,
  );
  return data;
}

export async function createL7Rule(
  policyId: string,
  payload: L7RuleCreateRequest,
): Promise<L7Rule> {
  const { data } = await http.post<L7Rule>(
    `/lbaas/l7policies/${policyId}/rules`,
    payload,
  );
  return data;
}

export async function updateL7Rule(
  policyId: string,
  ruleId: string,
  payload: L7RuleUpdateRequest,
): Promise<L7Rule> {
  const { data } = await http.put<L7Rule>(
    `/lbaas/l7policies/${policyId}/rules/${ruleId}`,
    payload,
  );
  return data;
}

export async function deleteL7Rule(
  policyId: string,
  ruleId: string,
): Promise<void> {
  await http.delete(`/lbaas/l7policies/${policyId}/rules/${ruleId}`);
}
